import Minilog from 'minilog';
import * as _ from 'underscore';

import {createMergeAction} from './base';
import {getStackTrace} from './core/exceptions';
import {addErrorMessage} from './feedback';
import * as Locale from './locale';
import {CreatePremisesParameters, PremisesV3API, subscribeToPremises,
        redirectToCabinetsByPremisesId, redirectToCabinets} from './premises';
import {updateUser} from './account';
import {GatewayAPI} from './gateways';
import {HttpStatusCodes} from './services';
let logger = Minilog('frontend.models.registration');

export function setServerErrors(validationErrors: {[input: string]: string[]}) {
    return createMergeAction(
        'registration_server_errors',
        {ui: {premises: {registration: {validationErrors}}}},
        {array_replace: true}
    );
}

/**
 * Set of status flags for the premises registration form.
 *
 * @type {{registered: boolean; error: boolean; authorized: boolean; submitting: boolean}}
 */
let defaultStatus = {
    registered: false,
    error: false,
    authorized: true,
    submitting: false
};


function gatewayStatusLoading() {
    return createMergeAction(
        'gateway_status_loading',
        {ui: {premises: {registration: {status: {loading: true}}}}}
    );
}

function gatewayStatusLoaded() {
    return createMergeAction(
        'gateway_status_loaded',
        {ui: {premises: {registration: {status: {loading: false}}}}}
    );
}

function registeringPremises() {
    return createMergeAction(
        'premises_registration_form_submit',
        {ui: {premises: {registration: {status: {submitting: true}}}}}
    );
}

function registeredPremises() {
    return createMergeAction(
        'premises_registration_form_submit_complete',
        {ui: {premises: {registration: {status: {submitting: false}}}}}
    );
}

export function addGatewayStatus(status: any) {
    return createMergeAction(
        'gateway_status',
        {ui: {premises: {registration: {status}}}}
    );
}

/**
 * Thunk: updates the global state with gateway information from the given
 * parameters
 */
export function getGatewayInformation(params: any) {
    return function (dispatch) {
        dispatch(gatewayStatusLoading());

        let {gatewayId} = params;
        // If no gateway id, user will input in form
        if (!gatewayId) {
            dispatch(gatewayStatusLoaded());
            return dispatch(addGatewayStatus(defaultStatus));
        }
        return GatewayAPI.get(gatewayId).then(function (r) {
            dispatch(gatewayStatusLoaded());
            if (r.data.premises != null) {
                // redirect user as this is already commissioned
                redirectToCabinetsByPremisesId(r.data.premises.id);
            } else {
                // We allow commissioning when premises is null
                dispatch(addGatewayStatus(defaultStatus));
            }
        }).catch(function(error) {
            let status = error.response.status;
            switch (status) {
                case HttpStatusCodes.CODE_403_FORBIDDEN:
                // User is trying to commission an existing GW they don't own
                dispatch(addErrorMessage(Locale.TEXT_REGISTER_UNAUTHORIZED));
                dispatch(addGatewayStatus({authorized: false}));
                break;

                case HttpStatusCodes.CODE_404_NOT_FOUND:
                // Gateway doesn't exist, they can commission it.
                dispatch(addGatewayStatus(defaultStatus));
                break;

                default:
                // We have an unknown error.
                dispatch(addErrorMessage(Locale.TEXT_REGISTER_UNKNOWN_ERROR));
                dispatch(addGatewayStatus({error: true}));
            }
            dispatch(gatewayStatusLoaded());
        });
    };
}

/**
 * Thunk: registers the premises - creates it, optionally updates user
 * contact information and subscribes to the newly created premises
 */
export function registerPremises(parameters: CreatePremisesParameters) {
    return function (dispatch) {
        dispatch(registeringPremises());

        // register user details first if specified
        let userDetails = _.pick(parameters, ['email', 'mobile']);
        let promise = Promise.resolve();
        if (!_.isEmpty(userDetails)) {
            promise = updateUser(dispatch, userDetails);
        }

        return promise.then(() => {
            // then create premises if user details were updated
            return PremisesV3API.create(parameters).then((response) => {
                let premises = response.data;
                let redirectFn = () => {
                    dispatch(registeredPremises());
                    redirectToCabinets(premises);
                };

                // only attempt to do subscribe if requested
                if (parameters.subscribe) {
                    return dispatch(subscribeToPremises(premises.id, true)).then(redirectFn);
                } else {
                    redirectFn();
                }
            });
        }).catch((error) => {
            if (error.response.status === HttpStatusCodes.CODE_400_BAD_REQUEST) {
                // only send on validation errors related to the registration
                // form, all others are sent as toast notifications.
                let notificationKeys = ['shadow_id'];

                let notifications = _.pick(error.data, notificationKeys);
                _.each(notifications, (value: string) => {
                    dispatch(addErrorMessage(value));
                });

                let formData = _.omit(error.data, notificationKeys);
                // ensure old validation errors are reset beforehand
                dispatch(setServerErrors(null));
                if (!_.isEmpty(formData)) {
                    dispatch(setServerErrors(formData));
                }
            } else {
                dispatch(addErrorMessage(Locale.TEXT_REGISTER_ERROR_UNABLE_TO_REGISTER));
            }
            logger.error(`Failed to register premises: ${error}`);
            logger.debug(getStackTrace(error));
            dispatch(registeredPremises());
        });
    };
}
